import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Box, SimpleGrid } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import type { DiscoverResult, DiscoverUserDto } from '../../app/models/discover'
import { AppCard, AppSkeleton, AppText, AuthGateCta, EmptyState } from '../../app/common/ui'
import { ProfileDiscoverCard } from './ProfileCard'

function SectionSkeletons () {
  return (
    <SimpleGrid
      cols={3}
      breakpoints={[
        { maxWidth: 992, cols: 2 },
        { maxWidth: 640, cols: 1 }
      ]}
      spacing="sm"
    >
      {[...Array(6)].map((_, i) => (
        <AppSkeleton key={i} height={130} radius={8} />
      ))}
    </SimpleGrid>
  )
}

interface SectionProps {
  label: string;
  users: DiscoverUserDto[];
  followState: Record<string, boolean>;
  onFollowToggle: (username: string) => void;
  onCardClick: (username: string) => void;
  loading: boolean;
  isLoggedIn: boolean;
}

function Section ({
  label,
  users,
  followState,
  onFollowToggle,
  onCardClick,
  loading,
  isLoggedIn
}: SectionProps) {
  return (
    <AppCard
      sx={{
        background: 'var(--gs-surface)',
        border: '1px solid var(--gs-border)',
        borderRadius: 8,
        padding: 'var(--gs-md)',
        marginBottom: 'var(--gs-md)'
      }}
    >
      <AppText
        sx={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          color: 'var(--gs-muted)',
          marginBottom: 12,
          fontWeight: 600
        }}
      >
        {label}
      </AppText>

      {loading
        ? <SectionSkeletons />
        : users.length === 0
          ? (
        <EmptyState
          title="You know everyone here"
          message="Check back soon for new members."
        />
            )
          : (
        <SimpleGrid
          cols={3}
          breakpoints={[
            { maxWidth: 992, cols: 2 },
            { maxWidth: 640, cols: 1 }
          ]}
          spacing="sm"
        >
          {users.map((u) => (
            <ProfileDiscoverCard
              key={u.username}
              user={u}
              isFollowing={followState[u.username] ?? u.isFollowing}
              onFollowToggle={onFollowToggle}
              onCardClick={onCardClick}
              isLoggedIn={isLoggedIn}
            />
          ))}
        </SimpleGrid>
            )}
    </AppCard>
  )
}

export default observer(function DiscoverPage () {
  const history = useHistory()
  const { userStore } = useStore()

  const [results, setResults] = useState<DiscoverResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [followState, setFollowState] = useState<Record<string, boolean>>({})

  useEffect(() => {
    agent.Profiles.discover()
      .then((data) => {
        setResults(data)
        const fs: Record<string, boolean> = {}
        ;[...data.topContributors, ...data.recentlyActive, ...data.newMembers].forEach((u) => {
          fs[u.username] = u.isFollowing
        })
        setFollowState(fs)
      })
      .catch(() => setResults({ topContributors: [], recentlyActive: [], newMembers: [] }))
      .finally(() => setLoading(false))
  }, [])

  const handleFollowToggle = async (username: string) => {
    const currently = followState[username] ?? false
    setFollowState((prev) => ({ ...prev, [username]: !currently }))
    try {
      if (currently) {
        await agent.Profiles.unfollow(username)
      } else {
        await agent.Profiles.follow(username)
      }
    } catch {
      setFollowState((prev) => ({ ...prev, [username]: currently }))
    }
  }

  const handleCardClick = (username: string) => {
    history.push(`/profiles/${username}`)
  }

  return (
    <Box sx={{ maxWidth: 1100, margin: '0 auto', padding: '0 var(--gs-lg) 48px' }}>
      <Box sx={{ marginBottom: 'var(--gs-lg)' }}>
        <AppText
          sx={{
            fontFamily: 'Oswald, sans-serif',
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--gs-text)',
            lineHeight: 1.2
          }}
        >
          DISCOVER PEOPLE
        </AppText>
        <AppText
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: 'var(--gs-muted)',
            marginTop: 'var(--gs-xs)'
          }}
        >
          Find firearms enthusiasts worth following.
        </AppText>
      </Box>

      {!userStore.isLoggedIn && (
        <AuthGateCta
          variant="banner"
          action="follow these members and build your community"
          pitch="Create a free account to follow collectors, share your builds, and earn your place on the leaderboard."
        />
      )}

      <Section
        label="Top Contributors"
        users={results?.topContributors ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />

      <Section
        label="Recently Active"
        users={results?.recentlyActive ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />

      <Section
        label="New Members"
        users={results?.newMembers ?? []}
        followState={followState}
        onFollowToggle={handleFollowToggle}
        onCardClick={handleCardClick}
        loading={loading}
        isLoggedIn={userStore.isLoggedIn}
      />
    </Box>
  )
})
